import { z } from 'zod';
import { DateString, JsonObject, LooseDate, Timestamp, Uuid } from './common.js';
import {
  ContentDateBasis,
  FactStatus,
  FactType,
  NegationReason,
  Sensitivity,
  Severity,
  SourceKind,
  SourceTier,
} from './vocab.js';

/**
 * The fact payload as the record and the brief render it (server-design.md
 * §3.1). One shape for both reads — the brief carries a subset of the same rows,
 * so the dashboard has one fact renderer.
 *
 * Deliberately absent: any score, confidence, rank or percentage. Corroboration
 * is a count of independent roots, rendered as "3 sources" — qualitative.
 */
export const Fact = z.object({
  lineage_id: Uuid,
  fact_type: FactType,

  /** Current version's `fields`, rendered in the reader's direction for pair and org-context facts. */
  fields: JsonObject,

  /**
   * The fact's two entity columns, and the **live** display name of each.
   *
   * Here because the sentence a pair fact renders ("Married to X", "Parent of
   * X") needs two things `fields` alone cannot honestly give: which side of the
   * pair the person being read is on, and what that person's counterpart is
   * *currently* called. Matching the extracted spelling in `fields.a.name`
   * against a display name is what printed 27 prod lines with the subject named
   * as their own counterparty — "Shawn Johnson East" and "Shawn Johnson" are one
   * person and two strings.
   *
   * The columns are **not** a direction source: `entity-merge` swaps them to
   * restore `subject = min(pair uuids)` and leaves `fields` alone. They are an
   * identity source, which is what side detection actually needs
   * (`pair-side.ts`).
   *
   * Both names are tombstone-aware and so both are nullable: a deleted person is
   * gone from every read (§8), and the fact row keeps its place and loses only
   * the name.
   */
  subject_entity_id: Uuid,
  counterparty_entity_id: Uuid.nullable(),
  subject_display_name: z.string().nullable(),
  counterparty_display_name: z.string().nullable(),

  status: FactStatus,
  /** Set only when `status = 'negated'`; drives the plain-language label on the row. */
  negation_reason: NegationReason.nullable(),

  /**
   * Server-computed severity class (server-design.md §7.1, exposed on the record
   * and brief payloads by the 2026-08-26 amendment). `'severe'` earns the amber
   * treatment whether or not an adjudication is pending; `'moderate'` and
   * `'none'` are the quieter and the clean readings of an adverse type (§3c/§3h);
   * `'info'` is the check that recorded something not adverse at all (2026-09-02);
   * `null` renders nothing.
   */
  severity: Severity,

  sensitivity: Sensitivity,

  /** Supporting claims grouped by independent root — never inflated (§7.2 step 4). */
  corroboration_count: z.number().int().nonnegative(),

  /** When this lineage first entered the record. */
  first_recorded_at: Timestamp,
  /**
   * Newest supporting claim's `content_date` — the honest "last confirmed <date>"
   * render (ui-design.md §3.8). Null when every supporting claim is undated.
   */
  last_corroborated: DateString.nullable(),

  /**
   * Present for windowed types (employment, board seats, memberships). Bounds
   * are `LooseDate`, not `DateString`: a window is a quote from a source, and a
   * source that said "2021" must not reach the reader as a January.
   */
  window: z
    .object({
      start_date: LooseDate.nullable(),
      end_date: LooseDate.nullable(),
    })
    .nullable(),

  /**
   * The queued (non-blocking) `claim_adjudication` gating this fact, if any.
   * There is no `blocking` flag here by construction: a blocking row means no
   * fact was written, so a blocking adjudication can never point at one.
   */
  pending_adjudication: z
    .object({
      decision_id: Uuid,
    })
    .nullable(),
});
export type Fact = z.infer<typeof Fact>;

/**
 * A claim held at ingest — its counterparty entity could not be resolved, so
 * **no lineage exists yet** (server-design.md §7.2 step 7). Held claims are a
 * pre-fact outcome, not a fact with a flag: they list on the record as pending
 * review and re-resolve when identity or entity events land.
 */
export const HeldClaim = z.object({
  claim_id: Uuid,
  fact_type: FactType,
  evidence_excerpt: z.string().nullable(),
  source_tier: SourceTier,
  content_date: DateString.nullable(),
});
export type HeldClaim = z.infer<typeof HeldClaim>;

/**
 * `GET /facts/:lineageId/claims` — the citation drill-down (server-design.md
 * §3.1). Citations resolve to stored evidence, never live URLs; `url` is quiet
 * plain text, not a link target.
 */
export const FactClaimEntry = z.object({
  claim_id: Uuid,
  source_kind: SourceKind,
  source_tier: SourceTier,
  evidence_excerpt: z.string().nullable(),
  evidence_id: Uuid.nullable(),
  /** First-party claims cite their prose instead of a retrieval. */
  prose_input_id: Uuid.nullable(),
  url: z.string().nullable(),
  retrieved_at: Timestamp.nullable(),
  content_date: DateString.nullable(),
  content_date_basis: ContentDateBasis,
  observed_at: Timestamp,
  /** Attached after the lineage was negated — corroboration that does not resurrect. */
  post_negation: z.boolean(),
});
export type FactClaimEntry = z.infer<typeof FactClaimEntry>;

export const FactClaimsResponse = z.object({
  entries: z.array(FactClaimEntry),
});
export type FactClaimsResponse = z.infer<typeof FactClaimsResponse>;
